/**
 * AnalyticsDashboard — Engine usage metrics
 * Route: /analytics (protected)
 */

import { useEffect, useState } from 'react'
import { BarChart2, Briefcase, Users, Mail, MessageSquare, DollarSign, ExternalLink, Activity } from 'lucide-react'
import { engineApiClient } from '@/lib/apiClient'

interface AnalyticsData {
  totalProjects:   number
  totalCandidates: number
  totalOutreach:   number
  totalPreScreens: number
  totalCostUsd:    number
  funnelStats?: {
    emailSent: number
    emailOpened: number
    linkClicked: number
    preScreenStarted: number
    preScreenCompleted: number
  }
  costByUsageType?: Array<{ usageType: string; totalCostUsd: number; calls: number }>
  costByProject?: Array<{ projectId: string; projectTitle: string; totalCostUsd: number; calls: number }>
}

function MetricTile({ icon, label, value, sub, color = 'text-[#FD802E]' }: {
  icon: React.ReactNode; label: string; value: string | number; sub?: string; color?: string
}) {
  return (
    <div className="bg-[#233D4C] rounded-xl border border-[#2a4a5c] p-5 flex flex-col gap-2">
      <div className="flex items-center gap-2 text-gray-400">
        <span className={color}>{icon}</span>
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-500">{sub}</p>}
    </div>
  )
}

function FunnelBar({ label, count, max, color }: { label: string; count: number; max: number; color: string }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-gray-400">{label}</span>
        <span className="text-white font-medium">{count} <span className="text-gray-500">({pct}%)</span></span>
      </div>
      <div className="h-2 bg-[#02182B] rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

export function AnalyticsDashboard() {
  const [data, setData] = useState<AnalyticsData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const base = (import.meta as unknown as { env: Record<string, string> }).env.VITE_ENGINE_API_BASE_URL ?? ''
    fetch(`${base}/api/engine/analytics`, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Server error ${res.status}`)
        return res.json() as Promise<AnalyticsData>
      })
      .then((d) => { setData(d); setLoading(false) })
      .catch((err) => { setError(err.message); setLoading(false) })
  }, [])

  return (
    <div className="min-h-screen bg-[#02182B] p-6">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center gap-3">
          <BarChart2 className="h-6 w-6 text-[#FD802E]" />
          <div>
            <h1 className="text-xl font-semibold text-white">Analytics</h1>
            <p className="text-sm text-gray-400">Searchline usage and performance overview</p>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
            <Activity size={18} className="animate-pulse text-[#FD802E]" />
            <span>Loading analytics…</span>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-400">
            Unable to load analytics. {error}
          </div>
        )}

        {data && (
          <>
            {/* Overview tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <MetricTile icon={<Briefcase size={16} />} label="Projects" value={data.totalProjects} sub="Searches created" />
              <MetricTile icon={<Users size={16} />} label="Candidates" value={data.totalCandidates} sub="Sourced & scored" color="text-blue-400" />
              <MetricTile icon={<Mail size={16} />} label="Outreach Sent" value={data.totalOutreach} sub="Emails delivered" color="text-sky-400" />
              <MetricTile icon={<MessageSquare size={16} />} label="Pre-Screens" value={data.totalPreScreens} sub="Completed by Erica" color="text-green-400" />
              <MetricTile icon={<DollarSign size={16} />} label="AI Cost" value={`$${data.totalCostUsd.toFixed(2)}`} sub="Total LLM spend" color="text-purple-400" />
            </div>

            {/* Funnel */}
            {data.funnelStats && (
              <div className="bg-[#233D4C] rounded-xl border border-[#2a4a5c] p-5">
                <h3 className="text-white font-semibold mb-4">Outreach Funnel</h3>
                <div className="space-y-3">
                  <FunnelBar label="Email Sent" count={data.funnelStats.emailSent} max={data.funnelStats.emailSent} color="bg-blue-400" />
                  <FunnelBar label="Email Opened" count={data.funnelStats.emailOpened} max={data.funnelStats.emailSent} color="bg-sky-400" />
                  <FunnelBar label="Link Clicked" count={data.funnelStats.linkClicked} max={data.funnelStats.emailSent} color="bg-yellow-400" />
                  <FunnelBar label="Pre-Screen Started" count={data.funnelStats.preScreenStarted} max={data.funnelStats.emailSent} color="bg-orange-400" />
                  <FunnelBar label="Pre-Screen Completed" count={data.funnelStats.preScreenCompleted} max={data.funnelStats.emailSent} color="bg-green-400" />
                </div>
              </div>
            )}

            {/* Cost by usage type */}
            {data.costByUsageType && data.costByUsageType.length > 0 && (
              <div className="bg-[#233D4C] rounded-xl border border-[#2a4a5c] p-5">
                <h3 className="text-white font-semibold mb-4">AI Cost by Activity</h3>
                <div className="space-y-2">
                  {data.costByUsageType.map((row) => (
                    <div key={row.usageType} className="flex items-center justify-between py-2 border-b border-[#2a4a5c] last:border-0">
                      <span className="text-gray-300 text-sm capitalize">{row.usageType.replace(/_/g, ' ')}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-gray-500 text-xs">{row.calls} calls</span>
                        <span className="text-[#FD802E] font-medium text-sm">${row.totalCostUsd.toFixed(4)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cost by project */}
            {data.costByProject && data.costByProject.length > 0 && (
              <div className="bg-[#233D4C] rounded-xl border border-[#2a4a5c] p-5">
                <h3 className="text-white font-semibold mb-4">Cost by Project</h3>
                <div className="space-y-2">
                  {data.costByProject.map((row) => (
                    <div key={row.projectId} className="flex items-center justify-between py-2 border-b border-[#2a4a5c] last:border-0">
                      <span className="text-gray-300 text-sm truncate max-w-xs">{row.projectTitle}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-gray-500 text-xs">{row.calls} calls</span>
                        <span className="text-[#FD802E] font-medium text-sm">${row.totalCostUsd.toFixed(4)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* PostHog user behaviour */}
            <div className="bg-[#233D4C] rounded-xl border border-[#2a4a5c] p-5">
              <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                <Activity size={16} className="text-[#FD802E]" />
                User Behaviour Analytics
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                Powered by PostHog. Tracks page views, click events, user journeys, and acquisition channels in real time.
              </p>
              <div className="grid grid-cols-2 gap-3 text-sm text-gray-400 mb-4">
                <div className="bg-[#02182B] rounded-lg p-3">📊 Page views by screen</div>
                <div className="bg-[#02182B] rounded-lg p-3">🔗 Acquisition channels</div>
                <div className="bg-[#02182B] rounded-lg p-3">🖱️ Click & interaction maps</div>
                <div className="bg-[#02182B] rounded-lg p-3">👤 User session replays</div>
              </div>
              <a
                href="https://posthog.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#FD802E] text-sm hover:underline"
              >
                Open PostHog dashboard <ExternalLink size={13} />
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
